import React from 'react';
import { Send, Eye, MousePointer, Users } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface EmailContentProps {
  animationDelay?: number;
}

const campaigns = [
  { name: 'Welcome', status: 'Active', opens: '45.2%' },
  { name: 'Launch', status: 'Scheduled', opens: '-' },
  { name: 'Newsletter', status: 'Draft', opens: '-' },
];

const EmailContent: React.FC<EmailContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div 
      className="flex-1 p-1.5 sm:p-2 lg:p-4 overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Stats Row */}
      <div 
        className="grid grid-cols-4 gap-1 sm:gap-1.5 lg:gap-2 mb-2 sm:mb-3 lg:mb-4"
        style={{ animation: `slideInFromTop 0.3s ease-out ${animationDelay + 100}ms both` }}
      >
        {[
          { icon: Send, label: 'Sent', value: '24.8K', color: '#3B82F6' },
          { icon: Eye, label: 'Opens', value: '42.5%', color: '#10B981' },
          { icon: MousePointer, label: 'Clicks', value: '8.7%', color: '#F59E0B' },
          { icon: Users, label: 'Subs', value: '5.8K', color: '#8B5CF6' },
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="p-1 sm:p-1.5 lg:p-2 rounded-lg text-center"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                animation: `scaleIn 0.3s ease-out ${animationDelay + 150 + (index * 60)}ms both`
              }}
            >
              <Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 mx-auto mb-0.5 lg:mb-1" style={{ color: stat.color }} />
              <div 
                className="text-[8px] sm:text-[10px] lg:text-sm font-bold"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                {stat.value}
              </div>
              <div className="text-[5px] sm:text-[6px] lg:text-[9px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Email Preview */}
      <div 
        className="grid grid-cols-2 gap-1.5 sm:gap-2 lg:gap-3"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 400}ms both` }}
      >
        {/* Email Builder Preview */}
        <div 
          className="rounded-lg overflow-hidden"
          style={{ 
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
          }}
        >
          <div 
            className="px-1.5 sm:px-2 py-1 sm:py-1.5 border-b flex items-center justify-between"
            style={{ 
              borderColor: isDark ? '#333333' : '#E5E5E5',
              backgroundColor: isDark ? '#252525' : '#FAFAFA'
            }}
          >
            <span className="text-[7px] sm:text-[8px] lg:text-[10px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              Builder
            </span>
            <div className="flex gap-0.5">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{ backgroundColor: '#EF4444' }} />
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{ backgroundColor: '#22C55E' }} />
            </div>
          </div>
          <div className="p-1.5 sm:p-2 lg:p-3">
            {/* Simulated email content */}
            <div 
              className="w-full h-5 sm:h-6 lg:h-8 rounded mb-1.5 sm:mb-2"
              style={{ 
                backgroundColor: isDark ? '#333333' : '#E5E5E5',
                animation: `shimmer 2s infinite`
              }}
            />
            <div className="space-y-1 sm:space-y-1.5">
              {[100, 85, 70, 90].map((width, i) => (
                <div 
                  key={i}
                  className="h-1 sm:h-1.5 lg:h-2 rounded"
                  style={{ 
                    width: `${width}%`,
                    backgroundColor: isDark ? '#2A2A2A' : '#F0F0F0',
                    animation: `fadeIn 0.3s ease-out ${animationDelay + 500 + (i * 80)}ms both`
                  }}
                />
              ))}
            </div>
            <div 
              className="mt-2 sm:mt-3 w-12 sm:w-16 lg:w-20 h-4 sm:h-5 lg:h-6 rounded flex items-center justify-center text-[6px] sm:text-[7px] lg:text-[9px] font-medium"
              style={{ 
                backgroundColor: '#3B82F6',
                color: '#FFFFFF',
                animation: `scaleIn 0.3s ease-out ${animationDelay + 700}ms both`
              }}
            >
              CTA
            </div>
          </div>
        </div>

        {/* Campaigns List */}
        <div 
          className="rounded-lg"
          style={{ 
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
          }}
        >
          <div 
            className="px-1.5 sm:px-2 py-1 sm:py-1.5 border-b"
            style={{ 
              borderColor: isDark ? '#333333' : '#E5E5E5',
              backgroundColor: isDark ? '#252525' : '#FAFAFA'
            }}
          >
            <span className="text-[7px] sm:text-[8px] lg:text-[10px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              Campaigns
            </span>
          </div>
          <div className="p-1 sm:p-1.5 lg:p-2 space-y-1 sm:space-y-1.5">
            {campaigns.map((campaign, index) => (
              <div
                key={index}
                className="p-1 sm:p-1.5 rounded flex items-center justify-between"
                style={{ 
                  backgroundColor: isDark ? '#252525' : '#FAFAFA',
                  animation: `slideInFromRight 0.3s ease-out ${animationDelay + 500 + (index * 80)}ms both`
                }}
              >
                <div>
                  <div 
                    className="text-[7px] sm:text-[8px] lg:text-[10px] font-medium"
                    style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                  >
                    {campaign.name}
                  </div>
                  <div 
                    className="text-[5px] sm:text-[6px] lg:text-[8px] px-0.5 sm:px-1 py-0.5 rounded inline-block mt-0.5"
                    style={{ 
                      backgroundColor: campaign.status === 'Active' 
                        ? (isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.1)')
                        : campaign.status === 'Scheduled'
                          ? (isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)')
                          : (isDark ? 'rgba(107,114,128,0.2)' : 'rgba(107,114,128,0.1)'),
                      color: campaign.status === 'Active' 
                        ? '#22C55E'
                        : campaign.status === 'Scheduled'
                          ? '#3B82F6'
                          : '#6B7280'
                    }}
                  >
                    {campaign.status}
                  </div>
                </div>
                <div className="text-right text-[6px] sm:text-[7px] lg:text-[9px]" style={{ color: isDark ? '#888888' : '#666666' }}>
                  {campaign.opens !== '-' && campaign.opens}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailContent;
